/* eslint-disable react/prop-types */
import { Descriptions } from 'antd';
import { useFormikContext } from 'formik';
import { keyBy, uniq } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    type FinderApplicationPublicAccessEntrypointSpecsFragment,
    type RouterFinderApplicationModuleFragment,
} from '../../../../../api/fragments';
import {
    CreateFinderApplicationPublicAccessEntrypointDocument,
    type CreateFinderApplicationPublicAccessEntrypointMutation,
    type CreateFinderApplicationPublicAccessEntrypointMutationVariables,
} from '../../../../../api/mutations/createFinderApplicationPublicAccessEntrypoint';
import {
    UpdateFinderApplicationPublicAccessEntrypointDocument,
    type UpdateFinderApplicationPublicAccessEntrypointMutation,
    type UpdateFinderApplicationPublicAccessEntrypointMutationVariables,
} from '../../../../../api/mutations/updateFinderApplicationPublicAccessEntrypoint';
import { type FinderApplicationPublicAccessEntrypointSettings } from '../../../../../api/types';
import InputField from '../../../../../components/fields/InputField';
import SelectField, { type SelectFieldProps } from '../../../../../components/fields/SelectField';
import { type EndpointImplementation, type EndpointViewPanelProps } from './types';

export const StyledBulletsList = styled.ul`
    margin-bottom: 0;
`;

const mapOption = (acc, module) => {
    if (module.__typename === 'FinderApplicationPublicModule') {
        return [...acc, { value: module.id, label: module.displayName }];
    }

    return acc;
};

type FormValues = FinderApplicationPublicAccessEntrypointSettings & { finderApplicationModuleIds?: string[] };

const implementation: EndpointImplementation<FinderApplicationPublicAccessEntrypointSpecsFragment, FormValues> = {
    getTitle: t => t('routerDetails:endpoints.FinderApplicationEntrypoint.title'),
    ViewPanelComponent: ({
        endpoint,
    }: EndpointViewPanelProps<FinderApplicationPublicAccessEntrypointSpecsFragment>) => (
        <Descriptions size="small" bordered>
            <Descriptions.Item label="Modules">
                {endpoint.finderApplicationModules?.length > 1 ? (
                    <StyledBulletsList>
                        {endpoint.finderApplicationModules.map(module => (
                            <li key={module.id}>{module.displayName}</li>
                        ))}
                    </StyledBulletsList>
                ) : (
                    <div>{endpoint.finderApplicationModules[0].displayName} </div>
                )}
            </Descriptions.Item>
            <Descriptions.Item label="Display Name">{endpoint.displayName}</Descriptions.Item>
        </Descriptions>
    ),
    // Finder vehicle always public
    getIsPrivate: () => false,
    FormComponent: ({ router }) => {
        const { t } = useTranslation(['routerDetails']);
        const { values } = useFormikContext<FormValues>();

        const finderApplicationModules: RouterFinderApplicationModuleFragment[] = useMemo(
            () =>
                router.company.modules.filter(
                    module => module.__typename === 'FinderApplicationPublicModule'
                ) as RouterFinderApplicationModuleFragment[],
            [router.company.modules]
        );

        const { finderApplicationModuleMapping, conditions } = useMemo(() => {
            const selectedFinderApplicationModules = finderApplicationModules.filter(({ id }) =>
                values.finderApplicationModuleIds?.includes(id)
            );

            return {
                finderApplicationModuleMapping: keyBy('id', selectedFinderApplicationModules),
                conditions: uniq(
                    selectedFinderApplicationModules.flatMap(({ finderVehicleConditions }) => finderVehicleConditions)
                ),
            };
        }, [finderApplicationModules, values.finderApplicationModuleIds]);

        const finderApplicationModule = useMemo(() => {
            if (!values.finderApplicationModuleIds?.length) {
                return null;
            }

            return finderApplicationModules.find(({ id, __typename }) => id === values.finderApplicationModuleIds[0]);
        }, [finderApplicationModules, values]);

        const options = useMemo(() => {
            if (!finderApplicationModule) {
                return router.company.modules.reduce<SelectFieldProps['options']>(mapOption, []);
            }

            return finderApplicationModules
                .filter(module => {
                    if (finderApplicationModule.__typename !== 'FinderApplicationPublicModule') {
                        return false;
                    }

                    const { vehicleModuleId, id, finderVehicleConditions, bankModuleId } = module;

                    if (finderApplicationModuleMapping[id]) {
                        return true;
                    }

                    return (
                        // should be the same vehicle module id
                        vehicleModuleId === finderApplicationModule.vehicleModuleId &&
                        // and also same financing id
                        bankModuleId === finderApplicationModule.bankModuleId &&
                        // should not allow if condition has been added before
                        !conditions.some(condition => finderVehicleConditions.includes(condition))
                    );
                })
                .reduce<SelectFieldProps['options']>(mapOption, []);
        }, [
            conditions,
            finderApplicationModule,
            finderApplicationModuleMapping,
            finderApplicationModules,
            router.company.modules,
        ]);

        return (
            <>
                <InputField
                    {...t('routerDetails:endpoints.fields.pathname', { returnObjects: true })}
                    name="pathname"
                />
                <InputField
                    {...t('routerDetails:endpoints.fields.displayName', { returnObjects: true })}
                    name="displayName"
                />
                <SelectField
                    {...t('routerDetails:endpoints.fields.finderApplicationModuleIds', { returnObjects: true })}
                    mode="multiple"
                    name="finderApplicationModuleIds"
                    options={options}
                    showSearch
                />
            </>
        );
    },
    getFormInitialValues: endpoint => ({
        pathname: endpoint.pathname,
        displayName: endpoint.displayName,
        finderApplicationModuleIds: endpoint.finderApplicationModules.map(module => module.id),
    }),
    submitCreation: (apolloClient, routerId, { finderApplicationModuleIds, ...values }) =>
        apolloClient.mutate<
            CreateFinderApplicationPublicAccessEntrypointMutation,
            CreateFinderApplicationPublicAccessEntrypointMutationVariables
        >({
            mutation: CreateFinderApplicationPublicAccessEntrypointDocument,
            variables: { routerId, settings: values, finderApplicationModuleIds },
        }),
    submitUpdate: (apolloClient, routerId, endpointId, { finderApplicationModuleIds, ...values }) =>
        apolloClient.mutate<
            UpdateFinderApplicationPublicAccessEntrypointMutation,
            UpdateFinderApplicationPublicAccessEntrypointMutationVariables
        >({
            mutation: UpdateFinderApplicationPublicAccessEntrypointDocument,
            variables: { routerId, endpointId, settings: values, finderApplicationModuleIds },
        }),
};

export default implementation;
